import AuthService from "@/services/auth.service"
import { getActivePinia } from "pinia"
import { localStorageService } from "@/services/localStorage.service"
import { tokenService } from "@/services/token.service"
import translationsService from "@/services/translations.service"
import { loadTranslations } from "@/locales"
import useToastManager from "./toastManager"
import { iriToUuid } from "@/services/helper"
import { inviteSuccessfullyAcepted } from "@/utils/toastAlert.js"

let refreshTokenTimeoutId = null

export const useAuthentication = () => {
  function scheduleRefreshToken() {
    if (!refreshTokenTimeoutId) {
      const token = localStorageService.getAccessToken()
      let { exp, iat } = tokenService.parseJwt(token)
      let expirationTime = exp - iat //time in sec

      // sec to milisec
      let timeToUpdateRefreshToken = (expirationTime / 2) * 1000

      const refreshTokenToSend = localStorageService.getAccessRefreshToken()
      refreshTokenTimeoutId = setTimeout(() => {
        refreshTokenToSend ? updateRefreshToken() : logout()
        refreshTokenTimeoutId = null
        scheduleRefreshToken()
      }, timeToUpdateRefreshToken)
    }
  }

  async function authSuccessHandler(response) {
    const { token, refresh_token } = response
    const tokenParsed = tokenService.parseJwt(token)
    localStorageService.setAccessRefreshToken(refresh_token)
    localStorageService.setAccessToken(token)
    localStorageService.setUserUuid(iriToUuid(tokenParsed["@id"]))
    localStorageService.setUserIri(tokenParsed["@id"])

    if (tokenParsed.account) localStorageService.setUserAccountIri(tokenParsed.account)

    scheduleRefreshToken()
  }

  const inviteAccepted = (hasInvitation) => {
    hasInvitation && inviteSuccessfullyAcepted()
  }

  async function login(payload) {
    const response = await AuthService.login(payload)
    authSuccessHandler(response)
    inviteAccepted(payload.invitation)
  }

  async function loginWithGoogle(idToken) {
    const response = await AuthService.loginWithGoogle({ idToken })
    authSuccessHandler(response)
  }

  async function signUp(payload) {
    if (!payload.plan) {
      payload.plan = `${import.meta.env.VITE_DEFAULT_SIGNUP_PLAN_IRI}`
    }
    const utmCookie = getCookieValue("utmValues")
    if (utmCookie) {
      payload.utm = parseUTMValues(utmCookie)
    }

    const response = await AuthService.signUp(payload)
    authSuccessHandler(response)
    inviteAccepted(payload.invitation)
  }

  function parseUTMValues(utmString) {
    let params = new URLSearchParams(utmString)
    let utmValues = {}

    for (let [key, value] of params.entries()) {
      utmValues[key] = value
    }

    return utmValues
  }

  function getCookieValue(name) {
    let cookies = document.cookie.split(";")
    for (let cookie of cookies) {
      let [cookieName, cookieValue] = cookie.trim().split("=")
      if (cookieName === name) {
        return decodeURIComponent(cookieValue)
      }
    }
    return null
  }

  function parseUTMValues(utmString) {
    let params = new URLSearchParams(utmString)
    let utmValues = {}

    for (let [key, value] of params.entries()) {
      utmValues[key] = value
    }

    return utmValues
  }

  function getCookieValue(name) {
    let cookies = document.cookie.split(";")
    for (let cookie of cookies) {
      let [cookieName, cookieValue] = cookie.trim().split("=")
      if (cookieName === name) {
        return decodeURIComponent(cookieValue)
      }
    }
    return null
  }

  async function signUpWithGoogle({ token, plan }) {
    const credentials = tokenService.parseJwt(token)

    const payload = {
      idToken: token,
      username: credentials.name,
      email: credentials.email,
      avatar: credentials.picture,
      browserLocale: navigator.language,
      plan
    }

    if (!payload.plan) {
      payload.plan = `${import.meta.env.VITE_DEFAULT_SIGNUP_PLAN_IRI}`
    }
    const utmCookieGoogle = getCookieValue("utmValues")
    if (utmCookieGoogle) {
      payload.utm = parseUTMValues(utmCookieGoogle)
    }

    const utmCookie = getCookieValue("utmValues")
    if (utmCookie) {
      payload.utm = parseUTMValues(utmCookie)
    }
    
    const response = await AuthService.signUpWithGoogle(payload)
    authSuccessHandler(response)
  }

  async function updateRefreshToken() {
    try {
      const response = await AuthService.updateToken(localStorageService.getAccessRefreshToken())
      if (translationsService.isLoadTranslationsNeedIt(response.tlu))
        loadTranslations({ forceLoadFromBackend: true })

      authSuccessHandler(response)
    } catch (error) {
      if (error.status === 401) {
        useToastManager().error({
          message: `Session expired`
        })
        setTimeout(logout, 250)
      }
    }
  }

  function logout() {
    localStorageService.removeAll()
    refreshTokenTimeoutId = null
    setTimeout(() => {
      getActivePinia()._s.forEach((store) => {
        if (store.resetState) store.resetState()
      })
    }, 50)

    if (window.location.pathname != "/login") {
      window.location.href = "/login"
    }
  }

  // async function hasValidToken() {
  //   return Boolean(
  //     localStorageService.getAccessRefreshToken() && localStorageService.getAccessToken()
  //   )
  // }

  async function userIsAuthenticated() {
    if (tokenService.isJwtRefreshNeedIt() && localStorageService.getAccessRefreshToken())
      await updateRefreshToken()

    return Boolean(
      localStorageService.getAccessRefreshToken() && localStorageService.getAccessToken()
    )
  }

  return {
    login,
    signUp,
    logout,
    loginWithGoogle,
    signUpWithGoogle,
    updateRefreshToken,
    userIsAuthenticated,
    scheduleRefreshToken,
    authSuccessHandler
  }
}
